import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, TrendingUp, Calendar, PieChart, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, casesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/cases`, { headers })
      ]);
      setStats(statsRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error("خطأ في تحميل التقارير:", error);
      toast.error("خطأ في تحميل البيانات: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getCasesByType = () => {
    const types = {};
    cases.forEach(c => {
      types[c.case_type] = (types[c.case_type] || 0) + 1;
    });
    return types;
  };

  const getCasesByStatus = () => {
    const statuses = {};
    cases.forEach(c => {
      statuses[c.status] = (statuses[c.status] || 0) + 1;
    });
    return statuses;
  };

  const getTypeLabel = (type) => {
    const labels = {
      civil: "مدني",
      criminal: "جنائي",
      commercial: "تجاري",
      labor: "عمل",
      personal_status: "أحوال شخصية",
      administrative: "إداري",
      employee_judiciary: "قضاء موظفين",
      investigation: "تحقيق",
      misdemeanor: "جنح",
      internal_security: "قوى الأمن الداخلي",
      military_court: "المحكمة العسكرية"
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: "نشطة",
      pending: "قيد الانتظار",
      closed: "مغلقة",
      archived: "مؤرشفة",
      won: "كسبنا",
      lost: "خسرنا"
    };
    return labels[status] || status;
  };

  const exportReport = () => {
    if (!cases || cases.length === 0) {
      toast.error("لا توجد بيانات للتصدير");
      return;
    }

    const BOM = "\uFEFF";
    const headers = ["عنوان القضية", "رقم القضية", "نوع القضية", "الحالة", "المحكمة", "القاضي", "المحامي", "المدعي", "المدعى عليه", "الوصف", "ملاحظات", "تاريخ الإنشاء"];
    const rows = cases.map(c => [
      c.title || "",
      c.case_number || "",
      getTypeLabel(c.case_type),
      getStatusLabel(c.status),
      c.court_name || "",
      c.judge_name || "",
      c.my_party_lawyer || "",
      c.plaintiff || "",
      c.defendant || "",
      (c.description || "").replace(/[\n\r]+/g, " "),
      (c.notes || "").replace(/[\n\r]+/g, " "),
      c.created_at ? new Date(c.created_at).toLocaleDateString("ar-IQ") : ""
    ]);

    const csvContent = BOM + [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    ).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `تقرير_القضايا_${new Date().toLocaleDateString("ar-IQ")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("تم تصدير التقرير بنجاح");
  };

  const casesByType = getCasesByType();
  const casesByStatus = getCasesByStatus();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="reports-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">التقارير والإحصائيات</h1>
            <p className="text-lg text-slate-600">تحليل شامل لأداء عملك القانوني</p>
          </div>
          <Button onClick={exportReport} className="gap-2" data-testid="export-report-button">
            <Download className="w-4 h-4" />
            تصدير التقرير
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <Card key={i} className="animate-pulse">
                <CardHeader><div className="h-6 bg-slate-200 rounded"></div></CardHeader>
                <CardContent><div className="h-20 bg-slate-200 rounded"></div></CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">إجمالي القضايا</CardTitle>
                    <FileText className="w-5 h-5" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.total_cases || 0}</div>
                  <p className="text-xs text-blue-100 mt-1">جميع قضاياك</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">قضايا نشطة</CardTitle>
                    <TrendingUp className="w-5 h-5" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.active_cases || 0}</div>
                  <p className="text-xs text-green-100 mt-1">قيد المتابعة</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">مواعيد قادمة</CardTitle>
                    <Calendar className="w-5 h-5" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.upcoming_appointments || 0}</div>
                  <p className="text-xs text-purple-100 mt-1">جلسات ومواعيد</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">إجابات مقدمة</CardTitle>
                    <BarChart3 className="w-5 h-5" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.questions_answered || 0}</div>
                  <p className="text-xs text-amber-100 mt-1">أسئلة تمت الإجابة عليها</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Cases by Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-600" />
                    توزيع القضايا حسب النوع
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(casesByType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <span className="text-slate-700">{getTypeLabel(type)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${(count / cases.length) * 100}%` }}
                            ></div>
                          </div>
                          <span className="font-semibold text-slate-900 w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cases by Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                    توزيع القضايا حسب الحالة
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(casesByStatus).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            status === 'active' ? 'bg-green-500' :
                            status === 'pending' ? 'bg-yellow-500' :
                            status === 'closed' ? 'bg-red-500' : 'bg-blue-500'
                          }`}></div>
                          <span className="text-slate-700">{getStatusLabel(status)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                status === 'active' ? 'bg-green-500' :
                                status === 'pending' ? 'bg-yellow-500' :
                                status === 'closed' ? 'bg-red-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${(count / cases.length) * 100}%` }}
                            ></div>
                          </div>
                          <span className="font-semibold text-slate-900 w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>مؤشرات الأداء</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {cases.length > 0 ? Math.round((stats?.active_cases / stats?.total_cases) * 100) : 0}%
                    </div>
                    <p className="text-sm text-slate-600">نسبة القضايا النشطة</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {stats?.questions_answered || 0}
                    </div>
                    <p className="text-sm text-slate-600">إجابات قانونية مقدمة</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {stats?.upcoming_appointments || 0}
                    </div>
                    <p className="text-sm text-slate-600">مواعيد مجدولة</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
